import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, CircularProgress } from '@mui/material';
import BasicFilterDropdown from '../BasicFilterDropdown/BasicFilterDropdown';
import BasicDropdownOption from '../BasicDropdownOption/BasicDropdownOption';
import { useWindowSize } from '../../../../../../utils/hooks';
import classes from './CommonSelectFilter.module.scss';
import { responsiveSizeJobs, responsiveSizeTimesheet } from '../../../../../../utils/constants';
import MainButton from '../../../../buttons/MainButton';
import { buttonVariants } from '../../../../buttons/types';
import colors from '../../../../../../scss/colors';

function Marker({ color }) {
  return <div style={{ width: '16px', height: '16px', borderRadius: '100%', backgroundColor: color }}>&nbsp;</div>;
}

export default function CommonSelectFilter({
  type,
  text,
  swiperTitle,
  data,
  selected,
  changeHandler,
  values,
  resetIndividual,
  handleSingleReset,
  count,
  pendingCount,
  footerHandler,
  isMultiSelect = false,
  isTextFullWidth = false
}) {
  const { width } = useWindowSize();
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const handleReset = () => {
    if (selected?.length) {
      if (isMultiSelect) {
        resetIndividual(type);
        setOpen(false);
      } else {
        setOpen(false);
        handleSingleReset();
      }
    } else {
      setOpen(false);
    }
  };

  const getDisplayText = () => {
    if (selected?.length > 1) return text;
    return selected?.length && selected[0]?.label ? selected[0].label : selected?.[0] || text;
  };

  return (
    <BasicFilterDropdown
      type={type}
      text={getDisplayText()}
      swiperTitle={swiperTitle}
      active={selected?.length}
      open={open}
      setOpen={setOpen}
      count={isMultiSelect && selected?.length > 1 ? selected?.length : false}
      pendingCount={pendingCount}
      filterCount={count}
      sxBox={{
        padding: isMultiSelect ? '58px 33px 33px 33px' : '39px 24px 33px 0px',
        maxHeight: '650px',
        maxWidth: '400px'
      }}
      showHandler={footerHandler}
      isCountVisible
      extraHeader={{
        enabled: !!selected?.length,
        label: t('common.filters.reset'),
        action: () => handleReset()
      }}
      isTextFullWidth={isTextFullWidth}
    >
      <div 
        className={classes.container}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Filter options"
      >
        <div className={classes.listContainer}>
          {data?.map((obj, index) => {
            let label;
            let value;
            let keyProp;

            if (typeof obj === 'string') {
              label = obj;
              value = obj;
              keyProp = obj;
            } else if (typeof obj === 'object' && obj !== null) {
              ({ label, value, key: keyProp } = obj);
            } else {
              label = '';
              value = '';
              keyProp = `unknown-${index}`;
            }

            const uniqueKey = value || keyProp || label || `item-${index}`;

            return <BasicDropdownOption
              key={uniqueKey}
              text={label}
              selected={!!selected?.length && selected.find((item) => (item?.value || item) === value)}
              clickHandler={(checked) => {
                const newData = selected?.filter((i) => (i.value || i) !== value);
                if (isMultiSelect) {
                  if (!checked) {
                    changeHandler({ ...values, [type]: newData });
                  } else {
                    changeHandler({
                      ...values,
                      [type]: selected.length ? [...selected, { label, value, key: keyProp }] : [{ label, value, key: keyProp }]
                    });
                  }
                } else {
                  changeHandler([label]);
                  setOpen(false);
                }
              }}
              multiSelect={isMultiSelect}
            />
          })}
        </div>

        <div className={classes.footerContainer}>
          {width > responsiveSizeJobs && (
            <MainButton
              variant={isMultiSelect ? buttonVariants.TEXT : buttonVariants.TERNARY}
              outlined
              rounded
              size="small"
              sx={{ fontSize: '13px' }}
              action={() => handleReset()}
            >
              {selected?.length ? t('common.filters.reset') : t('common.general.cancel')}
            </MainButton>
          )}
          {isMultiSelect && (
            <MainButton
              forceWidth="129px"
              rounded
              size="small"
                sx={{
                  fontSize: '13px'
                }}
                action={() => {
                  footerHandler();
                  setOpen(false);
                }}
              >
                {isMultiSelect &&
                  (pendingCount ? (
                    <CircularProgress sx={{ color: colors.WHITE }} size="20px" />
                  ) : (
                    `Show ${count || 0} results`
                  ))}
              </MainButton>
            )}
          </div>
      </div>
    </BasicFilterDropdown>
  );
}
